import { useArticleAttributesStore } from "@/data/stores/articleAttribute";



import { Format } from "@/data/util/Format";



import { translateByLocationLanguage } from "@/shared/localization/language";
import type { ICustomerArticleAttribute } from "@/data/model/Customer";
import { type AttributeDisplayType, AttributeFlagDisplayTo } from "@/data/model/Enums";


// one attribute can carry a combination of icons in a single class string, separated by a
// semicolon (e.g. "fa-light fa-cheese;fa-light fa-ban"). each part becomes its own <i> inside
// the attribute's .item-icon, laid out across in the order they were written
export function splitIconClasses(iconClass: string | undefined): Array<string> {
    if (iconClass == null) {
        return [];
    }

    return iconClass
        .split(";")
        .map((c) => c.trim())
        .filter((c) => c.length > 0);
}

// the html counterpart of ItemIcons.vue, for the views that build their markup as a string
export function renderItemIconsHtml(
    articleId: string | undefined,
    includeOrderedBefore: boolean = false,
    mainArticleId?: string
): string {
    const iconsHtml = useArticleAttributesStore()
        .iconAttributesForArticle(articleId, includeOrderedBefore, mainArticleId)
        .map((icon) => {
            const classes = splitIconClasses(icon.iconClass);
            if (classes.length === 0) {
                return "";
            }

            return (
                '<span class="item-icon" title="' +
                Format.htmlEscape(translateByLocationLanguage(icon.name)) +
                '">' +
                classes.map((c) => '<i class="' + Format.htmlEscape(c) + '"></i>').join("") +
                "</span>"
            );
        })
        .join("");

    if (iconsHtml.length === 0) {
        return "";
    }

    return '<span class="item-icons">' + iconsHtml + "</span>";
}

// the html counterpart of ItemFlags.vue. the audience is passed in because the kiosk store
// that decides it is app code, and this helper is reachable from the portal bundle
export function renderItemFlagsHtml(articleId: string | undefined, isOperatorView: boolean): string {
    const flags = useArticleAttributesStore()
        .flagAttributesForArticle(articleId)
        .filter((flag) => isAttributeDisplayedTo(flag, isOperatorView));

    if (flags.length === 0) {
        return "";
    }

    const flagsHtml = flags
        .map(
            (flag) =>
                '<div class="item-flag"><span class="label">' +
                Format.htmlEscape(translateByLocationLanguage(flag.name)) +
                "</span></div>"
        )
        .join("");

    return '<div class="item-flags">' + flagsHtml + "</div>";
}

export function hasAttributeDisplayType(value: AttributeDisplayType, flag: AttributeDisplayType): boolean {
    return (value & flag) === flag;
}

// a flag attribute carries the audience it is meant for; the other display types have none
// and are shown to everybody
export function isAttributeDisplayedTo(attribute: ICustomerArticleAttribute, isOperatorView: boolean): boolean {
    switch (attribute.flagDisplayTo) {
        case AttributeFlagDisplayTo.EndUsers:
            return !isOperatorView;
        case AttributeFlagDisplayTo.Operators:
            return isOperatorView;
        case AttributeFlagDisplayTo.EndUsersAndOperators:
            return true;
        default:
            return false;
    }
}